import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import Webcam from "react-webcam";
import { useInterview } from "../../../services/InterviewProgressContext";

const WebcamRecorder = ({
  setQuestions,
  mcqQuestionsCompleted,
  recording,
  setRecording,
  setCountdown,
  countdown,
  setVideoUrl,
  handleRelisten,
  isCameraAllowedRef,
  setStartVideo,
  isStoppingVideo,
  retryCountRef,
  currentStep,
}) => {
  const [answerId, setAnswerId] = useState(1);
  const videoRef = useRef(null);
  const [questionGenerated, setQuestionGenerated] = useState(false);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const candidateID = searchParams.get("candidate_id");
  const hasStartedVideo = useRef(false);
  const captureIntervalRef = useRef(null);
  /**
   * Context for the step of the interview
   */
  const { goToStep } = useInterview();

  let retryCount = 0;
  let maxWarningCount = 30;
  let maxWarning = false;

  const captureAndSendImage = async () => {
    if (currentStep == "completed" || currentStep == "reportGenerated") return;
    const imageSrc = videoRef.current.getScreenshot();

    if (imageSrc) {
      try {
        const blob = await (await fetch(imageSrc)).blob();
        const file = new File([blob], "face-profile.png", {
          type: "image/png",
        });

        const formData = new FormData();
        formData.append("image", file);
        await axios.post(
          `${process.env.REACT_APP_API_KEY}/start_video`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            params: {
              jd_id: id,
              candidate_id: candidateID,
            },
          }
        );
        getSuspiciousActivity();
      } catch (error) {
        retryCountRef.current++;
        if (retryCountRef.current < maxWarningCount + 1) {
          getSuspiciousActivity();
          toast.error(
            retryCountRef.current === maxWarningCount
              ? "This is your last warning or else the session will terminate!"
              : error.response.data.message ||
                  "You are violating the interview rules!",
            {
              style: { backgroundColor: "red", color: "white" },
            }
          );
        } else {
          goToStep("completed");
        }
      }
    }
  };

  const startWebcam = () => {
    if (!mcqQuestionsCompleted) {
      goToStep("mcqs");
    }
    captureIntervalRef.current = setInterval(() => {
      if (retryCountRef.current < maxWarningCount) {
        captureAndSendImage();
      }
      if (retryCountRef.current == maxWarningCount) {
        retryCount = retryCountRef.current;
        goToStep("completed");
      }
    }, 3000);
    setTimeout(() => {
      mcqQuestionsCompleted && generateQuestion();
    }, 4000);
  };

  const recordVideoApiCall = async () => {
    if (
      !isCameraAllowedRef ||
      hasStartedVideo.current ||
      isStoppingVideo.current
    )
      return;
    if (currentStep == "completed" || currentStep == "reportGenerated") return;
    setStartVideo(true);

    try {
      const record = await axios.get(
        `${process.env.REACT_APP_API_KEY}/start_video?jd_id=${id}&candidate_id=${candidateID}`,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      hasStartedVideo.current = true;
      retryCount = 0;
      setStartVideo(false);
    } catch (error) {
      retryCount++;
      setStartVideo(false);
      if (retryCount < maxWarningCount && !isStoppingVideo.current) {
        setTimeout(() => {
          if (
            (currentStep != "completed" || currentStep != "reportGenerated") &&
            !isStoppingVideo.current
          ) {
            recordVideoApiCall();
          }
        }, 3000);

        toast.error(
          retryCount === maxWarningCount - 1
            ? "This is your last warning or else the session will terminate!"
            : error?.response?.data?.message ||
                "You are violating the interview rules!",
          {
            style: { backgroundColor: "red", color: "white" },
          }
        );
      } else {
        isStoppingVideo.current = true;
        toast.error("Your session has been terminated!", {
          style: { backgroundColor: "red", color: "white" },
        });
      }
    }
  };

  const getSuspiciousActivity = async () => {
    if (!isCameraAllowedRef) return;
    const payload = {
      max_warning: maxWarningCount,
      retry_count: retryCountRef.current + 1,
    };
    if (retryCountRef.current < maxWarningCount) {
      try {
        const record = await axios.post(
          `${process.env.REACT_APP_API_KEY}/get_suspicious_activity_details?jd_id=${id}&candidate_id=${candidateID}`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
      } catch (error) {}
    }
  };

  const generateQuestion = async (ansID) => {
    setRecording(false);
    if (currentStep == "reportGenerated") {
      console.log("nfkjlgbsdkjbds");
      return;
    }
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_KEY
        }/get_next_question?jd_id=${id}&t=${Date.now()}&candidate_id=${candidateID}`,
        { headers: { "Content-Type": "application/json" } }
      );
      setQuestions(response?.data);
      setVideoUrl(response?.data?.video_url);
      setQuestionGenerated(true);
      setTimeout(() => {
        if (!maxWarning) {
          recordAnswer(ansID ? ansID + 1 : 1);
        }
      }, 10000);
    } catch (error) {
      goToStep("codingQuestions");
      retryCount = maxWarningCount + 10;
    }
  };

  const recordAnswer = async (ansID) => {
    const currentAnswerId = ansID || answerId;
    setAnswerId((prevId) => prevId + 1);
    setRecording(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
        const fileName = `answer-${currentAnswerId}.webm`;

        const formData = new FormData();
        formData.append("q_type", "answer");
        formData.append("id", currentAnswerId);
        formData.append("audio_file", audioBlob, fileName);

        stream.getTracks().forEach((track) => track.stop());

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_KEY}/record?jd_id=${id}&candidate_id=${candidateID}`,
            {
              method: "POST",
              body: formData,
            }
          );

          if (!response.ok) {
            throw new Error(`Upload failed: ${response.status}`);
          }

          if (!maxWarning) {
            compareAnswer(currentAnswerId);
          }
        } catch (uploadErr) {
          console.error("Error uploading audio:", uploadErr);
        }

        setRecording(false);
      };

      mediaRecorder.start();

      setTimeout(() => {
        mediaRecorder.stop();
      }, 20000);
    } catch (error) {
      console.error("Error accessing microphone or recording audio:", error);
      setRecording(false);
    }
  };

  const compareAnswer = async (ansID) => {
    setRecording(false);
    const payload = {
      id: ansID,
    };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}/compare?jd_id=${id}&candidate_id=${candidateID}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      if (!maxWarning) {
        mcqQuestionsCompleted && generateQuestion(ansID);
      }
    } catch (error) {
      if (!maxWarning) {
        mcqQuestionsCompleted && generateQuestion(ansID);
      }
      console.error("Error while comparing question answers:", error);
    }
  };

  const stopWebcam = () => {
    const videoElement = videoRef.current?.video;
    if (videoElement && videoElement.srcObject) {
      const tracks = videoElement.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoElement.srcObject = null;
    }
  };

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user",
  };

  useEffect(() => {
    if (mcqQuestionsCompleted) {
      captureAndSendImage();
      startWebcam();
    }
  }, []);

  useEffect(() => {
    if (currentStep === "completed" || currentStep == "reportGenerated") {
      stopWebcam();
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current); // stop face detection pings
      }
    }
  }, [currentStep]);

  useEffect(() => {
    return () => {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let timer;
    if (recording) {
      setCountdown(30);
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            clearInterval(timer);
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [recording]);

  useEffect(() => {
    if (mcqQuestionsCompleted) {
      generateQuestion();
    }
  }, [mcqQuestionsCompleted]);

  return (
    <div className="w-full">
      <div className="relative w-full h-[calc(100vh-190px)] border-2 border-gray-400 rounded-lg shadow-md flex items-center justify-center bg-gray-100">
        <Webcam
          audio={false}
          ref={videoRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          className="absolute w-full rounded-lg h-full object-cover"
        />
      </div>

      <div className="flex gap-3 mt-4 justify-center">
        <motion.button
          onClick={startWebcam}
          className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-600"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <i className="fa fa-play"></i> Start
        </motion.button>

        <motion.button
          className="bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-800"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <i className="fa fa-headset"></i> Contact Support
        </motion.button>
        <motion.button
          className="bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-800"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <i className="fa fa-envelope"></i> Email Support
        </motion.button>

        {questionGenerated && (
          <motion.button
            onClick={handleRelisten}
            className="bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-800"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <i class="fa-solid fa-repeat"></i>
            Re Listen
          </motion.button>
        )}
        {recording && (
          <div className="text-white font-semibold bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 bg-clip-text text-transparent animate-pulse rounded-md backdrop-blur-sm shadow-lg">
            Your answer recording will stop in {countdown} seconds
          </div>
        )}
      </div>
    </div>
  );
};
export default WebcamRecorder;
